import React, { useCallback, useMemo } from 'react'
import { FormikProps, useFormikContext } from 'formik'
import { NoteV2, SelectV4 } from '@labrav/react-components'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { SelectChangeEvent, Typography } from '@mui/material'
import { NET_PAYMENT_TERMS_AWS_DEFAULT } from '../../../modules/privateOffer/privateOfferCreation/utils/helperFunctions'
import { useStyles } from './NetPaymentTermsField.styles'

export { NET_PAYMENT_TERMS_AWS_DEFAULT }

export const NetPaymentTermsOptions = [
  {
    label: NET_PAYMENT_TERMS_AWS_DEFAULT,
    value: NET_PAYMENT_TERMS_AWS_DEFAULT,
  },
  { label: '15 days', value: '15' },
  { label: '30 days', value: '30' },
  { label: '45 days', value: '45' },
  { label: '60 days', value: '60' },
  { label: '90 days', value: '90' },
  { label: '120 days', value: '120' },
]

export type NetPaymentTermsContext = 'offer' | 'resaleAuthorization'

type NetPaymentTermsTooltip = {
  title: string
  content: string
}

export const NET_PAYMENT_TERMS_NOTE_BY_CONTEXT: Record<
  NetPaymentTermsContext,
  string
> = {
  offer: 'Credit card buyers are not eligible for custom net payment terms.',
  resaleAuthorization:
    'Set the maximum payment term the channel partner can offer in a Channel offer.',
}

export const NET_PAYMENT_TERMS_INFO_TOOLTIP_BY_CONTEXT: Record<
  NetPaymentTermsContext,
  NetPaymentTermsTooltip
> = {
  offer: {
    title: '',
    content:
      "Net payment terms determine the number of days between invoice issuance and payment due date. Net payment terms are available only for buyers with a pay-by-invoice payment method. Discuss payment terms with your buyer before selecting a custom payment term. If no option is selected then buyer's default AWS payment terms will apply.",
  },
  resaleAuthorization: {
    title: '',
    content:
      'Net payment terms determine the number of days between invoice issuance and payment due date. Net payment terms are available only for buyers with a pay-by-invoice payment method. If no payment term is selected, the channel partner cannot offer custom payment terms.',
  },
}

export const NET_PAYMENT_TERMS_INFO_TOOLTIP =
  NET_PAYMENT_TERMS_INFO_TOOLTIP_BY_CONTEXT.offer

export type NetPaymentTermsFieldProps = {
  formik?: FormikProps<Record<string, unknown>>
  fieldKey?: string
  showSectionTitle?: boolean
  showNote?: boolean
  context?: NetPaymentTermsContext
}

export const NetPaymentTermsField: React.FC<NetPaymentTermsFieldProps> = ({
  formik: formikProp,
  fieldKey = 'netPaymentTerms',
  showNote = true,
  context = 'offer',
}) => {
  const formikFromContext = useFormikContext<Record<string, unknown>>()
  const { values, errors, touched, setFieldValue, handleBlur } =
    formikProp ?? formikFromContext
  const { classes } = useStyles()

  const noteText = NET_PAYMENT_TERMS_NOTE_BY_CONTEXT[context]
  const infoTooltip = NET_PAYMENT_TERMS_INFO_TOOLTIP_BY_CONTEXT[context]

  const netPaymentTermsSelectField = useMemo(
    () => ({
      title: 'Net payment terms (Optional)',
      placeHolder: 'Net payment terms',
      humanReadableKey: 'Net payment terms',
      id: fieldKey,
      isRequired: false,
      isReadOnly: false,
      isCustom: false,
      defaultValue: '',
      dataType: 'enum' as DataType,
      options: NetPaymentTermsOptions,
    }),
    [fieldKey]
  )

  const handleNetPaymentTermsChange = useCallback(
    async (event: SelectChangeEvent) => {
      await setFieldValue(fieldKey, event.target.value)
    },
    [fieldKey, setFieldValue]
  )

  const fieldTouched = touched[fieldKey]
  const fieldError = errors[fieldKey]

  return (
    <div
      className={classes.root}
      data-testid="net-payment-terms-field"
      data-context={context}
    >
      {showNote && (
        <div className={classes.note}>
          <NoteV2
            description={
              <Typography className={classes.noteText}>{noteText}</Typography>
            }
          />
        </div>
      )}
      <div className={classes.field}>
        <SelectV4
          field={netPaymentTermsSelectField}
          value={(values[fieldKey] as string | undefined) ?? ''}
          onChange={handleNetPaymentTermsChange}
          onBlur={handleBlur}
          isError={!!fieldTouched && !!fieldError}
          errorText={fieldTouched && fieldError ? String(fieldError) : ''}
          showInsideInfoIcon
          showInsideInfoIconTooltip={infoTooltip}
        />
      </div>
    </div>
  )
}
